import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import useRouter from './useRouter';
import SidebarHelper from 'general/components/Sidebars/SidebarHelper';
import useGetMenuItemsList from 'general/components/Sidebars/MenuItemsList';
import AppRoute from 'general/constants/AppRoute';

// Routes not listed in sidebar menu but still accessible when authenticated
const MENU_EXEMPT_PATHS = [AppRoute.superDashboard];

export const useRoutePermission = (user, agencyRoleList, permissionList) => {
    const router = useRouter();
    const config = useSelector((state) => state.app.appConfig?.config);
    const poaUserId = _.find(config, (item) => item.key === 'poaUserId')?.value;
    const menuItems = useGetMenuItemsList(user, poaUserId, agencyRoleList);

    const getAllMenuPaths = (formattedItems) => {
        const paths = [];
        formattedItems.forEach((item) => {
            if (item.type === "item" && item.path) paths.push(item.path);
            if (item.subMenuItems?.length > 0) {
                item.subMenuItems.forEach((subItem) => {
                    if (subItem.path) paths.push(subItem.path);
                });
            }
        });
        return [...new Set(paths)];
    };

    const isPermissionReady = useMemo(() => {
        if (_.isEmpty(user)) return false;
        if (_.isEmpty(menuItems)) return false;
        return true;
    }, [user, menuItems]);

    const formattedItems = useMemo(() => {
        if (!isPermissionReady) return [];
        return SidebarHelper.checkPermissionList(menuItems, permissionList);
    }, [menuItems, permissionList, isPermissionReady]);

    const allAllowedPaths = useMemo(() => {
        if (!isPermissionReady) return [];
        return getAllMenuPaths(formattedItems);
    }, [formattedItems, isPermissionReady]);

    const firstAllowedPath = useMemo(() => {
        if (!isPermissionReady) return null;

        const firstMenuItem = formattedItems.find((item) => item.type === 'item');
        return firstMenuItem?.subMenuItems?.[0]?.path ?? firstMenuItem?.path ?? null;
    }, [formattedItems, isPermissionReady]);

    const normalizePath = (path) => {
        if (!path) return "/";
        return path.replace(/\/+$/, "") || "/";
    };

    const hasPermission = useMemo(() => {
        if (!isPermissionReady) return true;

        const currentPath = normalizePath(router.pathname);
        if (currentPath === "/") return true;

        const isExempt = MENU_EXEMPT_PATHS.some((p) => {
            const normalized = normalizePath(p);
            return currentPath === normalized || currentPath.startsWith(normalized + "/");
        });
        if (isExempt) return true;

        return allAllowedPaths.some((allowedPath) => {
            const normalizedAllowed = normalizePath(allowedPath);
            if (currentPath === normalizedAllowed) return true;
            if (normalizedAllowed !== "/" && currentPath.startsWith(normalizedAllowed + "/")) return true;
            return false;
        });
    }, [router.pathname, allAllowedPaths, isPermissionReady]);

    return { hasPermission, isPermissionReady, firstAllowedPath };
};
